<template>
  <div class="awb-backdrop" @click="$emit('close')"></div>
  <div class="awb-drawer" role="dialog" aria-modal="true" aria-labelledby="awb-drawer-title" @keydown.esc="$emit('close')">
    <div class="awb-drawer__head">
      <div v-if="row" class="d-flex flex-column gap-1">
        <div class="small awb-muted">{{ headline }}</div>
        <h2 id="awb-drawer-title" class="awb-big mb-0">{{ money(isPayout ? -row.amount : row.amount) }}</h2>
        <span class="awb-badge align-self-start" :class="statusClass(row)">{{ $t(statusKey(row)) }}</span>
      </div>
      <h2 v-else id="awb-drawer-title" class="awb-h2">…</h2>
      <button ref="close" type="button" class="awb-close" :aria-label="$t('airwallexBalance.close')" @click="$emit('close')">×</button>
    </div>

    <div v-if="row" class="awb-drawer__body">
      <!-- Payout: what it paid out and where it went. -->
      <template v-if="isPayout">
        <section>
          <h3 class="awb-h3">{{ $t("airwallexBalance.summary") }}</h3>
          <dl class="awb-dl">
            <template v-if="contents.unlinked_payment_count">
              <dt>{{ $t("airwallexBalance.payments") }} ({{ contents.payment_count }})</dt>
              <dd>{{ money(contents.net_earnings) }}</dd>
            </template>
            <template v-else>
              <dt>{{ $t("airwallexBalance.paymentsGross", { count: contents.payment_count }) }}</dt>
              <dd>{{ money(contents.gross_sales) }}</dd>
              <dt>{{ $t("airwallexBalance.fees") }}</dt>
              <dd>{{ money(-contents.fees) }}</dd>
            </template>
            <template v-if="contents.refunds">
              <dt>{{ $t("airwallexBalance.refunds") }}</dt>
              <dd>{{ money(-contents.refunds) }}</dd>
            </template>
            <template v-if="otherMovements">
              <dt>{{ $t("airwallexBalance.typeOther") }}</dt>
              <dd>{{ money(otherMovements) }}</dd>
            </template>
            <template v-if="contents.carried_over">
              <dt>{{ contents.carried_over > 0 ? $t("airwallexBalance.carriedIn") : $t("airwallexBalance.carriedOut") }}</dt>
              <dd>{{ money(contents.carried_over) }}</dd>
            </template>
            <dt class="awb-dl__total">{{ $t("airwallexBalance.paidOut") }}</dt>
            <dd class="awb-dl__total fw-bold">{{ money(-row.amount) }}</dd>
          </dl>
        </section>

        <section>
          <h3 class="awb-h3">{{ $t("airwallexBalance.sentTo") }}</h3>
          <dl class="awb-dl">
            <template v-if="row.transfer">
              <dt>{{ $t("airwallexBalance.beneficiary") }}</dt>
              <dd class="text-end">{{ row.transfer.beneficiary_name }}<div class="small awb-muted">{{ row.transfer.beneficiary_identifier }}</div></dd>
            </template>
            <dt>{{ $t("airwallexBalance.colMethod") }}</dt>
            <dd>{{ $t("airwallexBalance.payoutMethod") }}</dd>
            <dt>{{ $t("airwallexBalance.reference") }}</dt>
            <dd class="text-break">{{ row.transfer ? row.transfer.reference : rowRef(row) }}</dd>
          </dl>
        </section>

        <section>
          <h3 class="awb-h3 mb-1">{{ $t("airwallexBalance.includedTitle") }}</h3>
          <p class="small awb-muted">
            {{ row.previous_payout_at ? $t("airwallexBalance.includedSince", { date: dateTime(row.previous_payout_at) }) : $t("airwallexBalance.includedAll") }}
          </p>
          <p v-if="!row.items.length" class="awb-empty mb-0">{{ $t("airwallexBalance.nothingIncluded") }}</p>
          <table v-else class="awb-table">
            <thead>
              <tr>
                <th scope="col">{{ $t("airwallexBalance.colOrder") }}</th>
                <th scope="col">{{ $t("airwallexBalance.paidAt") }}</th>
                <th scope="col" class="awb-right">{{ $t("airwallexBalance.colNet") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in row.items" :key="item.id">
                <td>
                  <button type="button" class="awb-link text-start" @click="$emit('open', item.id)">{{ rowRef(item) }}</button>
                  <div v-if="item.category !== 'payment'" class="small awb-muted">{{ $t(typeKey[item.category]) }}</div>
                </td>
                <td class="awb-muted text-nowrap">{{ dateTime(item.created_at) }}</td>
                <td class="awb-right awb-num">{{ money(item.amount) }}</td>
              </tr>
            </tbody>
          </table>
        </section>
      </template>

      <!-- Payment / refund / other wallet movement. -->
      <template v-else>
        <section>
          <h3 class="awb-h3">{{ $t("airwallexBalance.breakdown") }}</h3>
          <dl class="awb-dl">
            <template v-if="row.gross_amount !== null">
              <dt>{{ $t("airwallexBalance.customerPaid") }}</dt>
              <dd>{{ money(row.gross_amount) }}</dd>
              <dt>{{ $t("airwallexBalance.megaposFee", { pct: feePct }) }}</dt>
              <dd>{{ money(-row.merchant_fee) }}</dd>
            </template>
            <dt class="awb-dl__total">{{ isDebit ? $t("airwallexBalance.takenBack") : $t("airwallexBalance.credited") }}</dt>
            <dd class="awb-dl__total fw-bold">{{ money(row.amount) }}</dd>
          </dl>
        </section>

        <section>
          <h3 class="awb-h3">{{ $t("airwallexBalance.timeline") }}</h3>
          <ol class="awb-timeline">
            <li v-for="step in timeline" :key="step.title" :class="{ 'awb-timeline--todo': step.todo }">
              <div class="d-flex flex-column">
                <span class="fw-bold small">{{ step.title }}</span>
                <span class="small awb-muted">{{ step.when }}</span>
              </div>
            </li>
          </ol>
          <button v-if="row.payout" type="button" class="awb-link small" @click="$emit('open', row.payout.id)">
            {{ $t("airwallexBalance.openPayout", { date: dateTime(row.payout.created_at) }) }} →
          </button>
        </section>

        <section>
          <h3 class="awb-h3">{{ $t("airwallexBalance.references") }}</h3>
          <dl class="awb-dl">
            <dt>{{ $t("airwallexBalance.order") }}</dt>
            <dd class="text-break">{{ row.transaction_id || "—" }}</dd>
            <dt>{{ $t("airwallexBalance.airwallexPayment") }}</dt>
            <dd class="text-break">{{ row.payment_intent_id || "—" }}</dd>
            <dt>{{ $t("airwallexBalance.account") }}</dt>
            <dd>{{ row.branch_name || $t("airwallexBalance.merchantAccount") }}</dd>
            <template v-if="row.payment_method">
              <dt>{{ $t("airwallexBalance.colMethod") }}</dt>
              <dd>{{ methodLabel(row.payment_method) }}</dd>
            </template>
          </dl>
        </section>

        <p v-if="row.category === 'payment'" class="awb-note awb-note--info">{{ $t("airwallexBalance.refundNote") }}</p>
      </template>
    </div>
  </div>
</template>

<script>
import PaymentService from "../../../services/payment.service";
import { money, dateTime, dateLong, methodLabel, typeKey, statusKey, statusClass, rowRef } from "./format";

export default {
  name: "balance-transaction-drawer",
  props: {
    id: { type: Number, required: true },
  },
  emits: ["open", "close"],
  data() {
    return { row: null, typeKey };
  },
  computed: {
    isPayout() {
      return this.row.category === "payout";
    },
    contents() {
      return this.row.contents || {};
    },
    isDebit() {
      return Number(this.row.amount) < 0;
    },
    headline() {
      if (this.isPayout) return this.$t("airwallexBalance.typePayout") + " · " + dateLong(this.row.created_at);
      const method = this.row.payment_method ? " · " + methodLabel(this.row.payment_method) : "";
      return this.$t(typeKey[this.row.category]) + method;
    },
    feePct() {
      return Number(this.row.gross_amount) ? ((this.row.merchant_fee / this.row.gross_amount) * 100).toFixed(1) : "0.0";
    },
    // Wallet rows in the payout that are neither payments nor refunds.
    otherMovements() {
      const known = this.contents.net_earnings - this.contents.refunds;
      return Math.round((this.contents.items_net - known) * 100) / 100;
    },
    timeline() {
      const row = this.row;
      const steps = [];
      if (row.gross_amount !== null) {
        steps.push({ title: this.$t("airwallexBalance.tPaid", { amount: money(row.gross_amount) }), when: dateLong(row.created_at) });
      }
      if (Number(row.amount) > 0) {
        steps.push({ title: this.$t("airwallexBalance.tCredited", { amount: money(row.amount) }), when: dateLong(row.created_at) });
      }
      steps.push(
        row.settled_at
          ? { title: this.$t("airwallexBalance.tSettled"), when: dateLong(row.settled_at) }
          : { title: this.$t("airwallexBalance.tSettled"), when: dateLong(row.estimated_settled_at), todo: true }
      );
      if (row.category === "payment") {
        steps.push(
          row.payout
            ? { title: this.$t("airwallexBalance.tPaidOut"), when: dateLong(row.payout.created_at) + " · " + rowRef({ category: "payout", description: row.payout.description }) }
            : { title: this.$t("airwallexBalance.tWaiting"), when: "", todo: true }
        );
      }
      return steps;
    },
  },
  watch: {
    id() {
      this.load();
    },
  },
  mounted() {
    this.load();
    this.$refs.close.focus();
  },
  methods: {
    money,
    dateTime,
    methodLabel,
    statusKey,
    statusClass,
    rowRef,
    load() {
      this.row = null;
      const id = this.id;
      PaymentService.airwallexBalanceTransaction(id).then((response) => {
        if (id === this.id && response.data.result.isSuccess) {
          this.row = response.data.data;
        }
      });
    },
  },
};
</script>
